import { useQuery } from '@tanstack/react-query';
import { RefreshCcw } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { LOADING_LABELS } from '@kexiaoman/shared';
import { adminApi, type OperationLogRow } from '@/lib/api';

export function LogsPage() {
  const [filters, setFilters] = useState<{
    command?: string;
    target_type?: string;
    target_id?: string;
    status?: 'success' | 'failed' | 'dry_run' | '';
    limit: number;
  }>({ limit: 100, status: '' });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['logs', filters],
    queryFn: () =>
      adminApi.listLogs({
        command: filters.command?.trim() || undefined,
        target_type: filters.target_type?.trim() || undefined,
        target_id: filters.target_id?.trim() || undefined,
        status: (filters.status || undefined) as 'success' | 'failed' | 'dry_run' | undefined,
        limit: filters.limit,
      }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold tracking-tight">审计日志</h2>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCcw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
          刷新
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        所有写操作都通过 <code className="text-xs bg-muted px-1 rounded">withOpLog</code> 包装；记录包含 operator / command /
        target / params / result。失败记录同样保留。
      </p>

      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-3 md:grid-cols-4">
            <Field label="command (精确)">
              <Input
                placeholder="user.lookup · seat.credits …"
                value={filters.command ?? ''}
                onChange={(e) => setFilters({ ...filters, command: e.target.value })}
              />
            </Field>
            <Field label="target_type">
              <Input
                placeholder="user / plan / code / route …"
                value={filters.target_type ?? ''}
                onChange={(e) => setFilters({ ...filters, target_type: e.target.value })}
              />
            </Field>
            <Field label="target_id (精确)">
              <Input
                placeholder="手机号 / UUID / code"
                value={filters.target_id ?? ''}
                onChange={(e) => setFilters({ ...filters, target_id: e.target.value })}
              />
            </Field>
            <Field label="status">
              <Select
                value={filters.status || 'all'}
                onValueChange={(v) =>
                  setFilters({ ...filters, status: (v === 'all' ? '' : v) as typeof filters.status })
                }
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  <SelectItem value="success">success</SelectItem>
                  <SelectItem value="failed">failed</SelectItem>
                  <SelectItem value="dry_run">dry_run</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </CardContent>
      </Card>

      {isLoading && <p className="text-muted-foreground">{LOADING_LABELS.loading}</p>}

      {data && (
        <>
          <p className="text-xs text-muted-foreground">共 {data.length} 条</p>
          <Table>
            <THead>
              <TR>
                <TH className="w-40">时间</TH>
                <TH>command</TH>
                <TH>target</TH>
                <TH>params</TH>
                <TH>status</TH>
                <TH>operator</TH>
              </TR>
            </THead>
            <TBody>
              {data.map((row) => (
                <LogRow key={row.id} row={row} />
              ))}
            </TBody>
          </Table>
        </>
      )}
    </div>
  );
}

function LogRow({ row }: { row: OperationLogRow }) {
  const [expanded, setExpanded] = useState(false);
  const statusColor =
    row.status === 'success' ? 'bg-green-100 text-green-800' :
    row.status === 'failed' ? 'bg-red-100 text-red-800' :
    'bg-gray-100 text-gray-600';

  return (
    <TR onClick={() => setExpanded((v) => !v)} className="cursor-pointer">
      <TD className="text-xs text-muted-foreground whitespace-nowrap">
        {new Date(row.created_at).toLocaleString('zh-CN')}
      </TD>
      <TD className="font-mono text-xs">{row.command}</TD>
      <TD className="text-xs">
        {row.target_type && <span className="text-muted-foreground">{row.target_type}:</span>}
        <span className={row.target_id ? 'ml-1' : ''}>{row.target_id ?? ''}</span>
      </TD>
      <TD className="text-xs max-w-[400px]">
        {expanded ? (
          <pre className="whitespace-pre-wrap font-mono text-[10px] bg-muted p-2 rounded max-h-80 overflow-auto">
            {JSON.stringify({ params: row.params, result: row.result }, null, 2)}
          </pre>
        ) : (
          <span className="text-muted-foreground">{row.params ? '(点击展开)' : '—'}</span>
        )}
      </TD>
      <TD>
        <span className={`inline-flex rounded px-2 py-0.5 text-xs ${statusColor}`}>{row.status}</span>
      </TD>
      <TD className="text-xs text-muted-foreground">{row.operator}</TD>
    </TR>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
